import { inject, Injectable } from '@angular/core';
import { PrismicDocument } from '@prismicio/client';
import { catchError, from, map, Observable, of } from 'rxjs';
import { LanguageService } from 'language';
import { CachedDataLoaderService } from 'utils';
import { CmsService } from './cms.service';
import { Channel } from './interfaces/channel.class';

@Injectable({
  providedIn: 'root',
})
export class ChannelService {
  private cms = inject(CmsService);
  private lang = inject(LanguageService);
  private dataLoader = inject(CachedDataLoaderService);

  private async getChannels(): Promise<Channel[]> {
    return this.dataLoader.get(this.stateKey, () => this.loadChannels());
  }

  public async findChannel(channelCode: string) {
    return (await this.getChannels()).find((c: Channel) => c.code === channelCode);
  }

  public getChannelLabel(channelCode: string): Observable<string> {
    if (!channelCode) return of('Unknown channel');

    return from(this.getChannels()).pipe(
      map(channels => {
        const matchingChannel = channels.find(c => c.code === channelCode);
        return matchingChannel?.name || 'Unknown channel';
      }),
      catchError(error => {
        if (!error?.wasCaught) {
          console.error('Error fetching channel data: channel not found in prismic', error);
        }
        return of('Unknown channel');
      })
    );
  }

  private get stateKey(): string {
    return `channels_${this.lang.current}`;
  }

  private async loadChannels() {
    const resp = await this.cms.getByType('channel');
    return resp.map((c: PrismicDocument) => new Channel(c));
  }
}
